import { useEffect, useState, useCallback } from 'react';
import adminAxios from '../lib/adminAxios';
import { Loader2, Check, AlertCircle, Package, Truck, CheckCircle2, XCircle, Clock, RefreshCw, Search, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending:    { label: 'Pending',    color: 'text-mb-muted',  icon: Clock },
  processing: { label: 'Processing', color: 'text-gold',      icon: Package },
  shipped:    { label: 'Shipped',    color: 'text-green-400', icon: Truck },
  delivered:  { label: 'Delivered',  color: 'text-green-300', icon: CheckCircle2 },
  cancelled:  { label: 'Cancelled',  color: 'text-red-400',   icon: XCircle },
};

const PAYMENT_STATUS_COLOR = {
  paid:    'text-gold',
  pending: 'text-mb-muted',
  failed:  'text-red-400',
  expired: 'text-mb-muted',
  refunded:'text-mb-muted',
};

function OrderRow({ order, onUpdated }) {
  const [tracking, setTracking] = useState(order.tracking_number || '');
  const [carrier, setCarrier]   = useState(order.tracking_carrier || '');
  const [notes, setNotes]       = useState(order.notes || '');
  const [status, setStatus]     = useState(order.fulfillment_status || 'pending');
  const [busy, setBusy]         = useState(false);
  const [result, setResult]     = useState(null);
  const [expanded, setExpanded] = useState(false);

  const isPaid = order.payment_status === 'paid';

  const save = async () => {
    setBusy(true);
    setResult(null);
    try {
      const payload = { fulfillment_status: status };
      if (tracking.trim()) payload.tracking_number = tracking.trim();
      if (carrier.trim())  payload.tracking_carrier = carrier.trim();
      if (notes.trim())    payload.notes = notes.trim();
      await adminAxios.patch(`${API}/admin/orders/${order.session_id}`, payload);
      setResult({ ok: true, msg: 'Saved.' });
      onUpdated();
    } catch (e) {
      setResult({ ok: false, msg: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const markShipped = async () => {
    setBusy(true);
    setResult(null);
    try {
      const payload = { fulfillment_status: 'shipped' };
      if (tracking.trim()) payload.tracking_number = tracking.trim();
      if (carrier.trim())  payload.tracking_carrier = carrier.trim();
      await adminAxios.patch(`${API}/admin/orders/${order.session_id}`, payload);
      setStatus('shipped');
      setResult({ ok: true, msg: 'Marked shipped.' });
      onUpdated();
    } catch (e) {
      setResult({ ok: false, msg: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const fulfillmentCfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const FIcon = fulfillmentCfg.icon;

  return (
    <div
      className={`hairline-b border-subtle transition-colors ${!isPaid ? 'opacity-50' : ''}`}
      data-testid={`order-row-${order.session_id}`}
    >
      {/* Summary row */}
      <div
        className="grid grid-cols-[90px_1fr_100px_110px_130px_auto] gap-3 items-center px-4 py-3 cursor-pointer hover:bg-mb-surface/60"
        onClick={() => setExpanded((v) => !v)}
        data-testid={`order-toggle-${order.session_id}`}
      >
        <div className="eyebrow-sm text-gold uppercase">{order.kind}</div>
        <div className="font-display text-[13px] text-mb truncate">{order.display_name || order.target_id}</div>
        <div className="font-display text-[13px] text-mb">{order.currency?.toUpperCase()} {Number(order.amount || 0).toFixed(2)}</div>
        <div className={`eyebrow-sm ${PAYMENT_STATUS_COLOR[order.payment_status] || 'text-mb-muted'}`}>
          {order.payment_status}
        </div>
        <div className={`flex items-center gap-1.5 eyebrow-sm ${fulfillmentCfg.color}`}>
          <FIcon size={11} /> {fulfillmentCfg.label}
        </div>
        <div className="eyebrow-sm text-mb-muted text-right">
          {new Date(order.created_at).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'2-digit' })}
        </div>
      </div>

      {/* Expanded fulfilment controls */}
      {expanded && (
        <div className="px-4 pb-5 pt-1 bg-mb-surface/30">
          {order.items?.length > 0 && (
            <div className="mb-4">
              <div className="eyebrow-sm text-mb-muted mb-1.5">Items</div>
              <div className="flex flex-wrap gap-1.5">
                {order.items.map((item, i) => (
                  <span key={i} className="eyebrow-sm text-mb px-2 py-0.5 hairline border-subtle">{item}</span>
                ))}
              </div>
            </div>
          )}

          {(order.buyer_name || order.buyer_email || order.shipping_address) ? (
            <div className="mb-4" data-testid={`order-ship-to-${order.session_id}`}>
              <div className="eyebrow-sm text-mb-muted mb-1.5">Ship to</div>
              <div className="text-[13px] text-mb leading-relaxed">
                {order.buyer_name && <div className="font-display">{order.buyer_name}</div>}
                {order.buyer_email && <div className="text-mb-muted">{order.buyer_email}</div>}
                {order.shipping_address && (
                  <div className="text-mb-muted">
                    {[order.shipping_address.line1, order.shipping_address.line2].filter(Boolean).join(', ')}
                    {order.shipping_address.city ? `, ${order.shipping_address.city}` : ''}
                    {order.shipping_address.state ? `, ${order.shipping_address.state}` : ''}
                    {order.shipping_address.postal_code ? ` ${order.shipping_address.postal_code}` : ''}
                    {order.shipping_address.country ? `, ${order.shipping_address.country}` : ''}
                  </div>
                )}
              </div>
            </div>
          ) : isPaid ? (
            <div className="mb-4 eyebrow-sm text-mb-muted italic" data-testid={`order-ship-to-missing-${order.session_id}`}>
              No buyer details captured yet — customer may not have returned to the success page.
            </div>
          ) : null}

          {order.custom_design_url && (
            <div className="mb-4" data-testid={`order-design-${order.session_id}`}>
              <div className="eyebrow-sm text-mb-muted mb-1.5">Logo artwork</div>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}${order.custom_design_url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 hairline border-subtle p-2 hover:border-gold transition-colors"
                data-testid={`order-design-link-${order.session_id}`}
              >
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}${order.custom_design_url}`}
                  alt="Customer-supplied logo"
                  className="h-10 w-10 object-contain bg-mb-surface"
                />
                <span className="eyebrow-sm text-gold">Download / view full size</span>
              </a>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr_1fr_1fr_auto_auto] gap-3 items-end">
            {/* Fulfilment status */}
            <div>
              <label className="mb-label">Fulfilment status</label>
              <select
                className="mb-input"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={{ appearance: 'none' }}
                data-testid={`order-status-select-${order.session_id}`}
              >
                {Object.entries(STATUS_CONFIG).map(([val, { label }]) => (
                  <option key={val} value={val} style={{ background: '#0a0a0a', color: '#F0EDE5' }}>{label}</option>
                ))}
              </select>
            </div>

            {/* Carrier */}
            <div>
              <label className="mb-label">Carrier</label>
              <input
                className="mb-input"
                placeholder="e.g. Royal Mail, DPD"
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                data-testid={`order-carrier-${order.session_id}`}
              />
            </div>

            {/* Tracking number */}
            <div>
              <label className="mb-label">Tracking number</label>
              <input
                className="mb-input"
                placeholder="e.g. JD0000123456"
                value={tracking}
                onChange={(e) => setTracking(e.target.value)}
                data-testid={`order-tracking-${order.session_id}`}
              />
            </div>

            {/* Notes */}
            <div>
              <label className="mb-label">Notes</label>
              <input
                className="mb-input"
                placeholder="Internal note"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid={`order-notes-${order.session_id}`}
              />
            </div>

            {/* Quick ship button */}
            {isPaid && status !== 'shipped' && status !== 'delivered' && (
              <button
                className="btn-gold whitespace-nowrap disabled:opacity-50"
                onClick={markShipped}
                disabled={busy}
                data-testid={`order-mark-shipped-${order.session_id}`}
              >
                {busy ? <Loader2 size={12} className="animate-spin" /> : <><Truck size={12} className="inline mr-1" />Ship</>}
              </button>
            )}

            {/* Save button */}
            <button
              className="btn-ghost whitespace-nowrap disabled:opacity-50"
              onClick={save}
              disabled={busy}
              data-testid={`order-save-${order.session_id}`}
            >
              {busy ? <Loader2 size={12} className="animate-spin" /> : 'Save'}
            </button>
          </div>

          {result && (
            <div className={`mt-2 eyebrow-sm flex items-center gap-2 ${result.ok ? 'text-gold' : 'text-red-400'}`}>
              {result.ok ? <Check size={11} /> : <AlertCircle size={11} />} {result.msg}
            </div>
          )}

          {order.shipped_at && (
            <div className="mt-2 eyebrow-sm text-mb-muted">
              Shipped {new Date(order.shipped_at).toLocaleString('en-GB', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}
              {order.shipping_email_sent && order.buyer_email && (
                <span className="text-gold"> · Tracking email sent to {order.buyer_email}</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const FILTER_OPTIONS = [
  { value: 'all',        label: 'All orders' },
  { value: 'paid',       label: 'Paid only' },
  { value: 'pending',    label: 'Pending fulfilment' },
  { value: 'processing', label: 'Processing' },
  { value: 'shipped',    label: 'Shipped' },
  { value: 'delivered',  label: 'Delivered' },
];

export default function FulfilmentPanel() {
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter]   = useState('paid');
  const [error, setError]     = useState(null);
  const [search, setSearch]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await adminAxios.get(`${API}/admin/payments/transactions`);
      setOrders(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = orders
    .filter((o) => {
      if (filter === 'all')        return true;
      if (filter === 'paid')       return o.payment_status === 'paid';
      return (o.fulfillment_status || 'pending') === filter;
    })
    .filter((o) => {
      const q = search.trim().toLowerCase();
      if (!q) return true;
      return [o.buyer_name, o.buyer_email, o.session_id]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(q));
    });

  const stats = {
    total:     orders.filter(o => o.payment_status === 'paid').length,
    pending:   orders.filter(o => o.payment_status === 'paid' && (!o.fulfillment_status || o.fulfillment_status === 'pending')).length,
    shipped:   orders.filter(o => o.fulfillment_status === 'shipped').length,
    delivered: orders.filter(o => o.fulfillment_status === 'delivered').length,
  };

  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-24" data-testid="admin-fulfilment-panel">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="eyebrow text-gold mb-4">Fulfilment</div>
          <h2 className="font-display text-3xl md:text-4xl text-mb">Orders · dispatch & tracking.</h2>
        </div>
        <button
          className="btn-ghost flex items-center gap-2 text-sm"
          onClick={load}
          disabled={loading}
          data-testid="fulfilment-refresh"
        >
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {[
          { label: 'Paid orders',    value: stats.total,     color: 'text-gold' },
          { label: 'Pending ship',   value: stats.pending,   color: 'text-mb-muted' },
          { label: 'Shipped',        value: stats.shipped,   color: 'text-green-400' },
          { label: 'Delivered',      value: stats.delivered, color: 'text-green-300' },
        ].map((s) => (
          <div key={s.label} className="bg-mb-surface hairline border-subtle p-4">
            <div className={`font-display text-3xl ${s.color} mb-1`}>{s.value}</div>
            <div className="eyebrow-sm text-mb-muted">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-mb-muted" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by customer name, email or order number…"
          className="mb-input pl-9 pr-9"
          data-testid="fulfilment-search-input"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-mb-muted hover:text-mb"
            data-testid="fulfilment-search-clear"
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {FILTER_OPTIONS.map(({ value, label }) => (
          <button
            key={value}
            className={`eyebrow-sm px-3 py-1.5 hairline border-subtle transition-colors ${
              filter === value ? 'bg-gold text-black border-gold' : 'text-mb-muted hover:text-mb'
            }`}
            onClick={() => setFilter(value)}
            data-testid={`fulfilment-filter-${value}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Orders table */}
      <div className="bg-mb-surface hairline border-subtle">
        {/* Header */}
        <div className="grid grid-cols-[90px_1fr_100px_110px_130px_auto] gap-3 items-center px-4 py-2 hairline-b border-subtle eyebrow-sm text-mb-muted">
          <div>Kind</div>
          <div>Item</div>
          <div>Amount</div>
          <div>Payment</div>
          <div>Fulfilment</div>
          <div className="text-right">Date</div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 px-4 py-6 text-mb-muted">
            <Loader2 size={14} className="animate-spin" />
            <span className="eyebrow-sm">Loading orders…</span>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 px-4 py-4 text-red-400">
            <AlertCircle size={14} />
            <span className="eyebrow-sm">{error}</span>
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="px-4 py-8 text-center eyebrow-sm text-mb-muted">
            No orders match this filter.
          </div>
        )}

        {!loading && filtered.map((order) => (
          <OrderRow
            key={order.session_id}
            order={order}
            onUpdated={load}
          />
        ))}
      </div>

      <div className="mt-4 eyebrow-sm text-mb-muted">
        Showing {filtered.length} of {orders.length} orders · Click any row to expand fulfilment controls
      </div>
    </section>
  );
}
